import { useApiInstance } from 'api';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import FieldSet from '@/components/FieldSet/FieldSet';
import Label from '@/components/Form/Label/Label';
import { CheckEmail, CheckRequired, composeValidators, MapServerErrors } from '@/components/FormAdapter/FormAdapter';
import Grid from '@/components/Grid/Grid';
import Paper from '@/components/Paper/Paper';
import Typography from '@/components/Typography/Typography';
import Button from '@/components/v2/Button/Button';
import { InputFormField } from '@/components/v2/FinalForm/Fields/InputFormField';
import { showNotifyNew } from '@/models/Notify';

import EmptyCheckMail from '../Empty/EmptyCheckMail';
import Link from '../Link/Link';

interface FormValues {
  email?: string;
}

const ForgotPasswordForm: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const fetchApi = useApiInstance();
  const resetPassword = async (values: FormValues) => {
    let result;
    try {
      result = await fetchApi.post('/company/users/reset-password', {
        email: values.email,
      });
    } catch (error) {
      result = error;
    }
    return result;
  };
  const [done, setDone] = useState<string | undefined>('');

  const onSubmit = async (values: FormValues) => {
    const result = await resetPassword(values);
    let errors;
    if (result.data && result.status === 200) {
      setDone(values.email);
    } else if (result.response && result.response.status === 422) {
      errors = MapServerErrors(result.response.data.errors);
    } else {
      const errorMessage =
        result.response && result.response.data && result.response.data.message
          ? result.response.data.message
          : intl.formatMessage({ id: 'app.error.passwordrecovery' });
      showNotifyNew(dispatch, {
        content: errorMessage,
        type: 'error',
      });
    }
    return errors;
  };

  return (
    <>
      {!done ? (
        <>
          <Paper>
            <div className="geecko-authform-block">
              <Typography variant="h2" component="h1" spacing={5}>
                <FormattedMessage id="app.auth.resetpassword" />
              </Typography>
              <Form
                onSubmit={onSubmit}
                render={({ handleSubmit, submitting }) => (
                  <form onSubmit={handleSubmit} className="geecko-form-setup-password__form">
                    <Typography variant="body1" component="p" style={{ marginBottom: 30 }}>
                      <FormattedMessage id="app.forms.resetpassword.description" />
                    </Typography>
                    <FieldSet spacing={5}>
                      <Label>{intl.formatMessage({ id: 'app.form.email.label' })}</Label>
                      <InputFormField
                        name="email"
                        type="email"
                        validate={composeValidators(CheckRequired, CheckEmail)}
                      />
                    </FieldSet>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Button variant="outline" type="submit" isWide disabled={submitting}>
                          <FormattedMessage id="app.auth.forgotpassword" />
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Link to="/auth">
                          <Button isWide variant="ghost" color="secondary" muted>
                            {intl.formatMessage({ id: 'app.auth.backtoauth' })}
                          </Button>
                        </Link>
                      </Grid>
                    </Grid>
                  </form>
                )}
              />
            </div>
          </Paper>
        </>
      ) : (
        <EmptyCheckMail text={intl.formatMessage({ id: 'app.recoverpassword.sendmail.text' }, { email: done })} />
      )}
    </>
  );
};

export default ForgotPasswordForm;
